<template>
  <div>
    <slot
      name="activator"
      :hide-popover="scheduleHidePopover"
      :create-popover="createPopover"
    />
    <div
      v-if="mouseIsOver"
      id="tt-tooltip"
      ref="tooltip"
      :class="[{'theme--dark': $vuetify.theme.dark}]"
      role="tooltip"
      @mouseenter="mouseInsidePopover"
      @mouseleave="hidePopover"
    >
      <div>{{ $t('label.table', [tabItems]) }}</div>
      <div>{{ reservation.contact.name || $t('placeholder.no_name') }} | {{ reservation.partySize }} p.</div>
      <div>
        {{ $localized.hourMinText(reservation.dateBegin) }} -
        {{ $localized.hourMinText(reservation.dateEnd) }}
      </div>
      <template v-if="hasExtraInfo">
        <hr class="my-1">
        <div class="font-weight-regular text-pre-wrap">
          {{ reservation.notes }}
        </div>
        <reservation-chips :reservation="reservation" />
      </template>
      <div
        id="tt-arrow"
        data-popper-arrow
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import Reservation from '@/model/Reservation';
import ReservationChips from '@/components/views/guests/ReservationChips.vue';
import { createPopper } from '@popperjs/core';
import {
  ref, PropType, computed, Ref, nextTick,
} from 'vue';

const props = defineProps({
  reservation: { type: Object as PropType<Reservation>, required: true },
});
const mouseIsOver = ref(false);
const hideTimeout = ref(undefined as undefined | number);

const hasExtraInfo = computed(() => (props.reservation.notes !== null && props.reservation.notes.length > 0)
        || props.reservation.paymentInfo !== undefined
        || props.reservation.service !== null
        || props.reservation.fixedEnding
        || props.reservation.label !== null
        || props.reservation.attachments.length > 0);

const tabItems = computed(() => props.reservation.tabItems.map((t) => t.no).join(', '));

const mouseInsidePopover = () => {
  window.clearTimeout(hideTimeout.value);
};

const hidePopover = () => { mouseIsOver.value = false; };
const scheduleHidePopover = () => {
  window.clearTimeout(hideTimeout.value);
  hideTimeout.value = window.setTimeout(() => { hidePopover(); }, 100);
};

const tooltip = ref(null) as Ref<null | HTMLElement>;
const createPopover = (el: any) => {
  if (mouseIsOver.value) { return; }
  mouseIsOver.value = true;
  const boundary = document.getElementById('tt-scrollable-container');

  nextTick(() => {
    const tt = (tooltip.value as HTMLElement);
    // console.log(`p: ${el} t: ${tooltip} b: ${boundary}`);

    createPopper(el, tt, {
      placement: 'auto',
      strategy: 'fixed',
      modifiers: [
        {
          name: 'preventOverflow',
          options: {
            boundary,
            padding: 8,
            altAxis: true,
          },
        },
      ],
    });
  });
};

</script>

<style lang="scss">

#tt-tooltip {
  position: static;
  display: inline-block;
  font-weight: bold;
  padding: 5px 10px;
  max-width: 70%;
  font-size: 13px;
  border-radius: 8px;
  z-index: 100;
  box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;

  background: $grey-2-color;
  color: $black-color;

  &.theme--dark {
    background: $grey-75-color;
    color: $white-color;
  }
}

#tt-arrow,
#tt-arrow::before {
  position: absolute;
  width: 8px;
  height: 8px;
  background: inherit;

}

#tt-arrow {
  visibility: hidden;
}

#tt-arrow::before {
  visibility: visible;
  content: '';
  transform: rotate(45deg);
}

#tt-tooltip[data-popper-placement^='top'] > #tt-arrow {
  bottom: -4px;
}

#tt-tooltip[data-popper-placement^='bottom'] > #tt-arrow {
  top: -4px;
}

#tt-tooltip[data-popper-placement^='left'] > #tt-arrow {
  right: -4px;
}

#tt-tooltip[data-popper-placement^='right'] > #tt-arrow {
  left: -4px;
}

</style>
